import * as React from "react";
import { useEffect, useRef } from "react";
import { useTranslations } from "../i18n/useTranslations";
import { useMenu } from "../Menu/useMenu";
import { useSearch } from "../Search/useSearch";
import { useUser } from "../user/useUser";
import { UserMenu } from "../UserMenu/UserMenu";
import { useUserMenu } from "../UserMenu/useUserMenu";
import { getGlobalThis, getMobileDevice, prefixedWithLang } from "../utils";
import { FreeTrialButton } from "./buttons/FreeTrialButton";
import { MenuButton } from "./buttons/MenuButton";
import { SearchButton } from "./buttons/SearchButton";
import { ShoppingListButton } from "./buttons/ShoppingListButton";
import { TrackingButton } from "./buttons/TrackingButton";
import { UserButton } from "./buttons/UserButton";
import { DD } from "./DD";

export function Header() {
  const ref = useRef<HTMLElement>();
  const { isUserMenuOpen } = useUserMenu();
  const { isOpen: isMenuOpen } = useMenu();
  const { isSearchOpen } = useSearch();
  const { loggedIn, hasSubscription, premium } = useUser();
  const { t, lang } = useTranslations();
  const isMobile = getMobileDevice(getGlobalThis().navigator).isMobile;

  /*
   * Decided during render, deliberately: computing these in an effect withheld the free trial
   * button and the member icons until after the first paint, which is the nav's slow-button
   * complaint. Anonymous is the render we can always be sure of, so it is what an unresolved
   * auth state shows; the integration supplies the user early enough (optimistically, from the
   * local session) that a member does not pass through it.
   */
  const shouldDisplayFreeTrial = !loggedIn || !premium || !hasSubscription;
  // D-63 P0: My tracking is a functional-access affordance — show it iff the user
  // has an active subscription (premium, incl. free-trial = ACTIVE), on both
  // viewports, so it never leads to a sales pitch. Shopping List was dropped entirely.
  const shouldDisplayIcons = premium;

  useEffect(() => {
    const el = ref.current;
    const cond = (isMenuOpen || isSearchOpen) && !isMobile;
    el.style.right = cond ? "15px" : "0";

    if (!isMobile) {
      el.classList.toggle("elm-z-20", !isMenuOpen);
      el.classList.toggle("elm-z-0", isMenuOpen);
    }
  }, [isMenuOpen, isSearchOpen, isMobile]);

  return (
    <header
      ref={ref}
      aria-label={t("Header")}
      className="elm-absolute elm-z-20 elm-p-2 elm-bg-white elm-w-full md:elm-max-w-screen-xl elm-select-none md:elm-px-0 md:elm-left-0 md:elm-right-0 md:elm-mx-auto"
    >
      <div className="elm-flex elm-w-full">
        <div className="elm-flex elm-justify-center elm-items-center elm-w-10 elm-h-10 elm-mx-1">
          <MenuButton />
        </div>
        <div className="elm-flex elm-justify-center elm-items-center elm-w-10 elm-h-10 elm-mx-1">
          <SearchButton />
        </div>
        <div className="elm-hidden md:elm-flex elm-h-10 elm-w-28 elm-ml-3">
          {/* I'm here to make space so the DD logo is centered */}
        </div>
        <div className="elm-flex elm-flex-auto elm-justify-center elm-items-center">
          <a
            tabIndex={0}
            data-track-label="link:home"
            className="elm-absolute elm-right-0 elm-left-0 elm-w-24 elm-mx-auto elm-flex elm-justify-center focus:elm-outline-none elm-px-2 elm-cursor-pointer elm-pointer-events-auto"
            href={prefixedWithLang(lang, "/")}
            target="_parent"
            title={t("Home page")}
            aria-label={t("Home page")}
          >
            <DD />
          </a>
        </div>
        {/* D-63 P0 order (ticket mock): [My tracking] [Shopping List] · Log in / avatar · [Free trial].
            Free trial (primary) sits rightmost; Log in (secondary) to its left. Tracking + Shopping
            List gate on `premium` (active members only). The logo is absolute-centered (equal
            margins) independent of this cluster. */}
        {shouldDisplayIcons && (
          <>
            <div className="elm-flex elm-justify-center elm-items-center elm-w-10 elm-h-10 elm-mx-1">
              <TrackingButton />
            </div>
            <div className="elm-flex elm-justify-center elm-items-center elm-w-10 elm-h-10 elm-mx-1">
              <ShoppingListButton />
            </div>
          </>
        )}
        <div
          className={`elm-static md:elm-relative elm-flex elm-justify-center elm-items-center elm-h-10 elm-mx-1 ${
            loggedIn ? "elm-w-10" : ""
          }`}
        >
          <UserButton />
          {isUserMenuOpen && <UserMenu />}
        </div>
        {shouldDisplayFreeTrial && (
          <div className="elm-flex elm-justify-center elm-items-center elm-h-10">
            <FreeTrialButton />
          </div>
        )}
      </div>
    </header>
  );
}
